/**
 * Connexion OpenAI Realtime via WebRTC (interface unifiée).
 * Le navigateur envoie le SDP offer à Famla → POST /v1/realtime/calls (clé API serveur).
 * Les tools Realtime (famla_*) sont exécutés ici : appels API Famla puis function_call_output sur le data channel.
 *
 * @see https://platform.openai.com/docs/guides/realtime-webrtc
 */
import { config } from "./config";
import { setAccessTokenCookie } from "../utils/authUtils";
function authHeaders(accessToken) {
    const h = { Accept: "application/json" };
    if (accessToken) {
        setAccessTokenCookie(accessToken);
        h.Authorization = `Bearer ${accessToken}`;
    }

    return h;
}

async function safeGetWebRtcStatsSummary(pc) {
    try {
        if (!pc || typeof pc.getStats !== "function") return undefined;
        const report = await pc.getStats();
        const out = {
            selectedCandidatePair: undefined,
            localCandidate: undefined,
            remoteCandidate: undefined,
            transport: undefined,
        };

        let selectedPair;
        report.forEach((stat) => {
            if (stat.type === "candidate-pair" && stat.selected) {
                selectedPair = stat;
            }
        });

        if (selectedPair) {
            out.selectedCandidatePair = {
                state: selectedPair.state,
                nominated: selectedPair.nominated,
                localCandidateId: selectedPair.localCandidateId,
                remoteCandidateId: selectedPair.remoteCandidateId,
                currentRoundTripTime: selectedPair.currentRoundTripTime,
                availableOutgoingBitrate: selectedPair.availableOutgoingBitrate,
            };

            const local = report.get(selectedPair.localCandidateId);
            const remote = report.get(selectedPair.remoteCandidateId);
            if (local) {
                out.localCandidate = {
                    candidateType: local.candidateType,
                    protocol: local.protocol,
                    address: local.address,
                    port: local.port,
                    url: local.url,
                };
            }
            if (remote) {
                out.remoteCandidate = {
                    candidateType: remote.candidateType,
                    protocol: remote.protocol,
                    address: remote.address,
                    port: remote.port,
                    url: remote.url,
                };
            }
        }

        report.forEach((stat) => {
            if (stat.type === "transport" && stat.selectedCandidatePairId) {
                out.transport = {
                    selectedCandidatePairId: stat.selectedCandidatePairId,
                    dtlsState: stat.dtlsState,
                };
            }
        });

        return out;
    } catch (_) {
        return undefined;
    }
}

async function reportClientWebRtcError({ base, accessToken, interviewId, payload }) {
    try {
        await fetch(`${base}/voice-realtime/client-error`, {
            method: "POST",
            headers: { ...authHeaders(accessToken), "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ interview_id: interviewId, ...payload }),
        });
    } catch (_) {
        /* ignore reporting errors */
    }
}


/**
 * @param {object} event — événement parsé du data channel (ex. response.done)
 * @param {RTCDataChannel} dc
 * @param {string} interviewId
 * @param {string} [accessToken]
 * @param {string} base — API base URL (sans slash final)
 * @param {() => void} [onPersistSuccess]
 * @param {(detail: { next_question?: string, ok?: boolean, status_code?: number, description?: string }) => void} [onPersistDetail]
 */
async function handleFamlaRealtimeTools(
    event,
    dc,
    interviewId,
    accessToken,
    base,
    onPersistSuccess,
    onPersistDetail
) {
    if (event.type !== "response.done" || !event.response?.output) return;

    for (const item of event.response.output) {
        if (item.type !== "function_call") continue;

        const { name, call_id: callId, arguments: argsStr } = item;
        let outputPayload;

        try {
            if (name === "famla_get_interview_context") {
                const r = await fetch(
                    `${base}/voice-realtime/context/${encodeURIComponent(interviewId)}`,
                    { headers: authHeaders(accessToken), credentials: "include" }
                );
                const j = await r.json();
                outputPayload = {
                    ok: j.status_code === 200,
                    context: j.data,
                    description: j.description,
                };
            } else if (name === "famla_save_interview_answer") {
                const args = argsStr ? JSON.parse(argsStr) : {};
                const r = await fetch(`${base}/voice-realtime/submit-answer`, {
                    method: "POST",
                    headers: { ...authHeaders(accessToken), "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                        interview_id: interviewId,
                        content: args.content,
                        answer_id: args.answer_id || undefined,
                    }),
                });
                const j = await r.json();
                const ok = j.status_code === 200;
                outputPayload = {
                    ok,
                    next_question: j.data?.question_text,
                    answer_id: j.data?.id,
                    step: j.data?.step,
                    description: j.description,
                };
                if (typeof onPersistDetail === "function") {
                    try {
                        onPersistDetail({
                            ok,
                            next_question: j.data?.question_text,
                            step: j.data?.step,
                            status_code: j.status_code,
                            description: j.description,
                        });
                    } catch (_) {
                        /* ignore */
                    }
                }
                if (ok && typeof onPersistSuccess === "function") {
                    try {
                        onPersistSuccess();
                    } catch (_) {
                        /* ignore */
                    }
                }
            } else {
                outputPayload = { ok: false, error: `Unknown tool: ${name}` };
            }
        } catch (err) {
            outputPayload = { ok: false, error: String(err?.message || err) };
        }

        if (dc.readyState === "open" && callId) {
            dc.send(
                JSON.stringify({
                    type: "conversation.item.create",
                    item: {
                        type: "function_call_output",
                        call_id: callId,
                        output: JSON.stringify(outputPayload),
                    },
                })
            );
            requestFollowUpResponse(dc, name, outputPayload);
        }
    }
}

/**
 * Tools Realtime — contributeur Data Collect (jeton d’invitation, pas de JWT).
 * @param {object} event
 * @param {RTCDataChannel} dc
 * @param {string} invitationToken
 * @param {string} base
 * @param {() => void} [onPersistSuccess]
 * @param {(detail: object) => void} [onPersistDetail]
 */
async function handleCollectVoiceTools(event, dc, invitationToken, base, onPersistSuccess, onPersistDetail) {
    if (event.type !== "response.done" || !event.response?.output) return;

    for (const item of event.response.output) {
        if (item.type !== "function_call") continue;

        const { name, call_id: callId, arguments: argsStr } = item;
        let outputPayload;

        try {
            if (name === "famla_get_collect_voice_context") {
                const r = await fetch(
                    `${base}/collect-contributor/voice/context/${encodeURIComponent(invitationToken)}`,
                    { headers: { Accept: "application/json" }, credentials: "omit" }
                );
                const j = await r.json();
                outputPayload = {
                    ok: j.status_code === 200,
                    context: j.data,
                    description: j.description,
                };
            } else if (name === "famla_save_collect_voice_message") {
                const args = argsStr ? JSON.parse(argsStr) : {};
                const r = await fetch(`${base}/collect-contributor/voice/submit`, {
                    method: "POST",
                    headers: { Accept: "application/json", "Content-Type": "application/json" },
                    credentials: "omit",
                    body: JSON.stringify({
                        invitation_token: invitationToken,
                        content: args.content,
                    }),
                });
                const j = await r.json();
                const ok = j.status_code === 200;
                outputPayload = {
                    ok,
                    next_question: j.data?.question_text,
                    answer_id: j.data?.id,
                    step: j.data?.step,
                    description: j.description,
                };
                if (typeof onPersistDetail === "function") {
                    try {
                        onPersistDetail({
                            ok,
                            next_question: j.data?.question_text,
                            step: j.data?.step,
                            status_code: j.status_code,
                            description: j.description,
                        });
                    } catch (_) {
                        /* ignore */
                    }
                }
                if (ok && typeof onPersistSuccess === "function") {
                    try {
                        onPersistSuccess();
                    } catch (_) {
                        /* ignore */
                    }
                }
            } else {
                outputPayload = { ok: false, error: `Unknown tool: ${name}` };
            }
        } catch (err) {
            outputPayload = { ok: false, error: String(err?.message || err) };
        }

        if (dc.readyState === "open" && callId) {
            dc.send(
                JSON.stringify({
                    type: "conversation.item.create",
                    item: {
                        type: "function_call_output",
                        call_id: callId,
                        output: JSON.stringify(outputPayload),
                    },
                })
            );
            requestFollowUpResponse(dc, name, outputPayload);
        }
    }
}

/**
 * Tools Realtime - owner setup Data Collect (JWT required).
 * @param {object} event
 * @param {RTCDataChannel} dc
 * @param {string} collectionId
 * @param {string} [accessToken]
 * @param {string} base
 * @param {() => void} [onPersistSuccess]
 * @param {(detail: object) => void} [onPersistDetail]
 */
async function handleOwnerCollectVoiceTools(
    event,
    dc,
    collectionId,
    accessToken,
    base,
    onPersistSuccess,
    onPersistDetail
) {
    if (event.type !== "response.done" || !event.response?.output) return;

    for (const item of event.response.output) {
        if (item.type !== "function_call") continue;

        const { name, call_id: callId, arguments: argsStr } = item;
        let outputPayload;

        try {
            if (name === "famla_get_owner_collect_voice_context") {
                const r = await fetch(
                    `${base}/collections/${encodeURIComponent(collectionId)}/owner-voice/context`,
                    { headers: authHeaders(accessToken), credentials: "include" }
                );
                const j = await r.json();
                outputPayload = {
                    ok: j.status_code === 200,
                    context: j.data,
                    description: j.description,
                };
            } else if (name === "famla_save_owner_collect_voice_message") {
                const args = argsStr ? JSON.parse(argsStr) : {};
                const r = await fetch(
                    `${base}/collections/${encodeURIComponent(collectionId)}/owner-voice/submit`,
                    {
                        method: "POST",
                        headers: { ...authHeaders(accessToken), "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({
                            collection_id: collectionId,
                            content: args.content,
                        }),
                    }
                );
                const j = await r.json();
                const ok = j.status_code === 200;
                outputPayload = {
                    ok,
                    next_question: j.data?.question_text,
                    description: j.description,
                };
                if (typeof onPersistDetail === "function") {
                    try {
                        onPersistDetail({
                            ok,
                            next_question: j.data?.question_text,
                            status_code: j.status_code,
                            description: j.description,
                        });
                    } catch (_) {
                        /* ignore */
                    }
                }
                if (ok && typeof onPersistSuccess === "function") {
                    try {
                        onPersistSuccess();
                    } catch (_) {
                        /* ignore */
                    }
                }
            } else {
                outputPayload = { ok: false, error: `Unknown tool: ${name}` };
            }
        } catch (err) {
            outputPayload = { ok: false, error: String(err?.message || err) };
        }

        if (dc.readyState === "open" && callId) {
            dc.send(
                JSON.stringify({
                    type: "conversation.item.create",
                    item: {
                        type: "function_call_output",
                        call_id: callId,
                        output: JSON.stringify(outputPayload),
                    },
                })
            );
            requestFollowUpResponse(dc, name, outputPayload);
        }
    }
}

/**
 * Même logique métier que le texte : invitation à inviter un rôle à la session de cartographie.
 * Sans ça, le modèle peut « passer » la question ou rester silencieux.
 */
function isStakeholderInvitationQuestion(text) {
    if (!text || typeof text !== "string") return false;
    const s = text.toLowerCase();
    return (
        s.includes("souhaitez-vous") ||
        s.includes("would you like to invite") ||
        (s.includes("inviter") && (s.includes("session") || s.includes("participer") || s.includes("cartographie"))) ||
        (s.includes("invite") && s.includes("process mapping")) ||
        (s.includes("participer") && s.includes("cartographie")) ||
        (s.includes("participate") && s.includes("mapping session")) ||
        (s.includes("stakeholder") && (s.includes("include") || s.includes("detected") || s.includes("inclure")))
    );
}

/**
 * Après function_call_output, déclenche une réponse modèle. Pour famla_save_interview_answer,
 * si next_question est une invitation collaborateur, impose la lecture intégrale à l'oral.
 * @param {RTCDataChannel} dc
 * @param {string} toolName
 * @param {object} [outputPayload]
 */
function requestFollowUpResponse(dc, toolName, outputPayload) {
    if (dc.readyState !== "open") return;

    const afterSave =
        "The save tool finished. The JSON output field next_question is the written transcript (next question only — no recap of the user's answer). " +
        "You MUST respond with spoken audio (not silence). In the process language: you may add a brief oral acknowledgment first, " +
        "then ask next_question. Do not merge commentary into next_question; spoken thanks or paraphrase are voice-only, not saved.";

    const afterSaveInvitation =
        "STAKEHOLDER INVITATION (same as text UI with Oui/Non). " +
        "The user must hear the FULL invitation before answering. " +
        "Speak ONLY from the tool output field next_question: read that ENTIRE sentence aloud in its language, " +
        "clearly, including the role name (e.g. direction, ETL) and the invitation to the process mapping session. " +
        "Do not skip, summarize, or replace it with a shorter question. No long preamble — at most a 3-word bridge, then read verbatim:\n\n";

    const afterContext =
        "The famla_get_interview_context tool returned data. Continue the interview in voice using that context; " +
        "if you need to speak, stay conversational.";

    let instructions;
    if (
        toolName === "famla_save_interview_answer" ||
        toolName === "famla_save_collect_voice_message" ||
        toolName === "famla_save_owner_collect_voice_message"
    ) {
        const nq = outputPayload?.next_question;
        if (nq && isStakeholderInvitationQuestion(nq)) {
            instructions = afterSaveInvitation + String(nq).trim();
        } else {
            instructions = afterSave;
        }
    } else if (
        toolName === "famla_get_interview_context" ||
        toolName === "famla_get_collect_voice_context" ||
        toolName === "famla_get_owner_collect_voice_context"
    ) {
        const inv = outputPayload?.context?.invitation_to_mapping_session;
        const q = outputPayload?.context?.question_text;
        if (inv && q && isStakeholderInvitationQuestion(q)) {
            instructions = afterSaveInvitation + String(q).trim();
        } else {
            instructions = afterContext;
        }
    } else {
        instructions = undefined;
    }

    const response = instructions
        ? { instructions, output_modalities: ["audio"] }
        : { output_modalities: ["audio"] };

    dc.send(
        JSON.stringify({
            type: "response.create",
            response,
        })
    );
}

function stopAll(peer, stream, audio) {
    try {
        peer.getSenders().forEach((s) => s.track && s.track.stop());
        peer.close();
    } catch (_) {
        /* ignore */
    }
    stream.getTracks().forEach((t) => t.stop());
    audio.srcObject = null;
    if (audio.parentNode) {
        audio.parentNode.removeChild(audio);
    }
}

/**
 * @param {object} opts
 * @param {string} opts.interviewId
 * @param {string} [opts.clientShownPendingQuestion] — dernière question affichée (sync avec le fil texte)
 * @param {string} [opts.accessToken]
 * @param {(evt: object) => void} [opts.onDataChannelMessage]
 * @param {(err: Error) => void} [opts.onError]
 * @param {() => void} [opts.onPersistSuccess] — après famla_save_interview_answer réussi (ex. recharger le fil)
 * @param {(detail: { next_question?: string, ok?: boolean }) => void} [opts.onPersistDetail]
 * @param {(active: boolean) => void} [opts.onVoiceSessionActiveChange] — WebRTC connecté / arrêté
 * @param {() => void} [opts.onStarterHintDismiss] — une fois : parole utilisateur ou audio assistant (masquer « Dites bonjour… »)
 * @returns {Promise<{ pc: RTCPeerConnection, dc: RTCDataChannel, audioElement: HTMLAudioElement, stop: () => void }>}
 */
export async function startOpenAIRealtimeWebRTC({
    interviewId,
    accessToken,
    clientShownPendingQuestion,
    onDataChannelMessage,
    onError,
    onPersistSuccess,
    onPersistDetail,
    onVoiceSessionActiveChange,
    onStarterHintDismiss,
}) {
    const base = config.api.baseUrl.replace(/\/$/, "");
    const url = `${base}/voice-realtime/webrtc`;

    const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    const audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    audioEl.setAttribute("playsinline", "true");
    document.body.appendChild(audioEl);

    pc.ontrack = (e) => {
        audioEl.srcObject = e.streams[0];
    };

    const ms = await navigator.mediaDevices.getUserMedia({
        audio: {
            echoCancellation: true,
            noiseSuppression: true,
        },
    });
    ms.getTracks().forEach((track) => pc.addTrack(track, ms));

    let starterHintDismissed = false;
    const maybeDismissStarterHint = () => {
        if (starterHintDismissed || typeof onStarterHintDismiss !== "function") return;
        starterHintDismissed = true;
        try {
            onStarterHintDismiss();
        } catch (_) {
            /* ignore */
        }
    };

    const dc = pc.createDataChannel("oai-events");
    dc.addEventListener("message", (e) => {
        let event;
        try {
            event = JSON.parse(e.data);
        } catch {
            return;
        }
        const t = event.type;
        if (
            t === "input_audio_buffer.speech_started" ||
            t === "input_audio_buffer.committed" ||
            t === "conversation.item.input_audio_transcription.completed" ||
            t === "response.output_audio.delta" ||
            t === "response.output_audio_transcript.delta" ||
            t === "response.audio.delta" ||
            t === "response.audio_transcript.delta"
        ) {
            maybeDismissStarterHint();
        }
        if (onDataChannelMessage) {
            onDataChannelMessage(event);
        }
        void handleFamlaRealtimeTools(
            event,
            dc,
            interviewId,
            accessToken,
            base,
            onPersistSuccess,
            onPersistDetail
        );
    });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const headers = {
        Accept: "application/sdp, application/json",
        "Content-Type": "application/json",
        ...authHeaders(accessToken),
    };

    const resp = await fetch(url, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
            interview_id: interviewId,
            sdp: offer.sdp || "",
            ...(clientShownPendingQuestion
                ? { client_shown_pending_question: clientShownPendingQuestion }
                : {}),
        }),
    });

    const ct = resp.headers.get("Content-Type") || "";

    if (!resp.ok) {
        let msg = await resp.text();
        try {
            const j = JSON.parse(msg);
            msg = j.description || j.detail || msg;
        } catch (_) {
            /* keep text */
        }
        const err = new Error(msg || `HTTP ${resp.status}`);
        stopAll(pc, ms, audioEl);
        throw err;
    }

    if (ct.includes("application/json")) {
        const j = await resp.json();
        stopAll(pc, ms, audioEl);
        throw new Error(j.description || j.detail || "Voice session error");
    }

    const answerSdp = await resp.text();
    await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

    if (typeof onVoiceSessionActiveChange === "function") {
        onVoiceSessionActiveChange(true);
    }

    const emitWebRtcFailure = async (reason) => {
        if (!onError) return;
        const iceState = pc.iceConnectionState;
        const pcState = pc.connectionState;
        const gatherState = pc.iceGatheringState;
        const signalingState = pc.signalingState;
        const extra = `pc=${pcState} ice=${iceState}`;

        const stats = await safeGetWebRtcStatsSummary(pc);
        void reportClientWebRtcError({
            base,
            accessToken,
            interviewId,
            payload: {
                reason,
                pc_state: pcState,
                ice_state: iceState,
                ice_gathering_state: gatherState,
                signaling_state: signalingState,
                user_agent: navigator.userAgent,
                stats,
            },
        });

        // Most frequent real-world cause when API signaling succeeds (HTTP 200) but WebRTC fails:
        // corporate networks/firewalls blocking UDP/STUN.
        if (iceState === "failed" || iceState === "disconnected") {
            onError(
                new Error(
                    `${reason} (pc=${pcState} ice=${iceState} gather=${gatherState} signal=${signalingState}). Network may block WebRTC (UDP/STUN). Try another network/VPN or allow STUN/TURN.`
                )
            );
            return;
        }
        onError(
            new Error(
                `${reason} (pc=${pcState} ice=${iceState} gather=${gatherState} signal=${signalingState}).`
            )
        );
    };

    pc.addEventListener("connectionstatechange", () => {
        if (pc.connectionState === "failed") {
            void emitWebRtcFailure("WebRTC connection failed");
        }
    });

    pc.addEventListener("iceconnectionstatechange", () => {
        if (pc.iceConnectionState === "failed") {
            void emitWebRtcFailure("WebRTC ICE negotiation failed");
        }
    });

    pc.addEventListener("icecandidateerror", (e) => {
        // Keep this log lightweight but useful; surfaced error stays user-friendly.
        try {
            // eslint-disable-next-line no-console
            console.warn("[Voice WebRTC] icecandidateerror", {
                errorCode: e.errorCode,
                errorText: e.errorText,
                url: e.url,
                address: e.address,
                port: e.port,
            });
        } catch (_) {
            /* ignore */
        }
    });

    const stop = () => {
        try {
            dc.close();
        } catch (_) {
            /* ignore */
        }
        if (typeof onVoiceSessionActiveChange === "function") {
            onVoiceSessionActiveChange(false);
        }
        stopAll(pc, ms, audioEl);
    };

    return { pc, dc, audioElement: audioEl, stop };
}

/**
 * WebRTC Realtime — session contributeur Data Collect (lien magique).
 * @param {object} opts
 * @param {string} opts.invitationToken
 * @param {string} [opts.clientShownPendingQuestion]
 * @param {string} [opts.accessToken] — ignoré (pas d’auth)
 * @param {(evt: object) => void} [opts.onDataChannelMessage]
 * @param {(err: Error) => void} [opts.onError]
 * @param {() => void} [opts.onPersistSuccess]
 * @param {(detail: object) => void} [opts.onPersistDetail]
 * @param {(active: boolean) => void} [opts.onVoiceSessionActiveChange]
 * @param {() => void} [opts.onStarterHintDismiss]
 */
export async function startOpenAIRealtimeWebRTCCollect({
    invitationToken,
    clientShownPendingQuestion,
    onDataChannelMessage,
    onError,
    onPersistSuccess,
    onPersistDetail,
    onVoiceSessionActiveChange,
    onStarterHintDismiss,
}) {
    const base = config.api.baseUrl.replace(/\/$/, "");
    const url = `${base}/collect-contributor/voice/webrtc`;

    const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    const audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    audioEl.setAttribute("playsinline", "true");
    document.body.appendChild(audioEl);

    pc.ontrack = (e) => {
        audioEl.srcObject = e.streams[0];
    };

    const ms = await navigator.mediaDevices.getUserMedia({
        audio: {
            echoCancellation: true,
            noiseSuppression: true,
        },
    });
    ms.getTracks().forEach((track) => pc.addTrack(track, ms));

    let starterHintDismissed = false;
    const maybeDismissStarterHint = () => {
        if (starterHintDismissed || typeof onStarterHintDismiss !== "function") return;
        starterHintDismissed = true;
        try {
            onStarterHintDismiss();
        } catch (_) {
            /* ignore */
        }
    };

    const dc = pc.createDataChannel("oai-events");
    dc.addEventListener("message", (e) => {
        let event;
        try {
            event = JSON.parse(e.data);
        } catch {
            return;
        }
        const t = event.type;
        if (
            t === "input_audio_buffer.speech_started" ||
            t === "input_audio_buffer.committed" ||
            t === "conversation.item.input_audio_transcription.completed" ||
            t === "response.output_audio.delta" ||
            t === "response.output_audio_transcript.delta" ||
            t === "response.audio.delta" ||
            t === "response.audio_transcript.delta"
        ) {
            maybeDismissStarterHint();
        }
        if (onDataChannelMessage) {
            onDataChannelMessage(event);
        }
        void handleCollectVoiceTools(event, dc, invitationToken, base, onPersistSuccess, onPersistDetail);
    });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const headers = {
        Accept: "application/sdp, application/json",
        "Content-Type": "application/json",
    };

    const resp = await fetch(url, {
        method: "POST",
        headers,
        credentials: "omit",
        body: JSON.stringify({
            invitation_token: invitationToken,
            sdp: offer.sdp || "",
            ...(clientShownPendingQuestion ? { client_shown_pending_question: clientShownPendingQuestion } : {}),
        }),
    });

    const ct = resp.headers.get("Content-Type") || "";

    if (!resp.ok) {
        let msg = await resp.text();
        try {
            const j = JSON.parse(msg);
            msg = j.description || j.detail || msg;
        } catch (_) {
            /* keep text */
        }
        const err = new Error(msg || `HTTP ${resp.status}`);
        stopAll(pc, ms, audioEl);
        throw err;
    }

    if (ct.includes("application/json")) {
        const j = await resp.json();
        stopAll(pc, ms, audioEl);
        throw new Error(j.description || j.detail || "Voice session error");
    }

    const answerSdp = await resp.text();
    await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

    if (typeof onVoiceSessionActiveChange === "function") {
        onVoiceSessionActiveChange(true);
    }

    pc.addEventListener("connectionstatechange", () => {
        if (pc.connectionState === "failed" && onError) {
            onError(new Error("WebRTC connection failed"));
        }
    });

    const stop = () => {
        try {
            dc.close();
        } catch (_) {
            /* ignore */
        }
        if (typeof onVoiceSessionActiveChange === "function") {
            onVoiceSessionActiveChange(false);
        }
        stopAll(pc, ms, audioEl);
    };

    return { pc, dc, audioElement: audioEl, stop };
}

/**
 * WebRTC Realtime - owner setup session Data Collect (JWT).
 * @param {object} opts
 * @param {string} opts.collectionId
 * @param {string} opts.accessToken
 * @param {string} [opts.clientShownPendingQuestion]
 * @param {(evt: object) => void} [opts.onDataChannelMessage]
 * @param {(err: Error) => void} [opts.onError]
 * @param {() => void} [opts.onPersistSuccess]
 * @param {(detail: object) => void} [opts.onPersistDetail]
 * @param {(active: boolean) => void} [opts.onVoiceSessionActiveChange]
 * @param {() => void} [opts.onStarterHintDismiss]
 */
export async function startOpenAIRealtimeWebRTCOwner({
    collectionId,
    accessToken,
    clientShownPendingQuestion,
    onDataChannelMessage,
    onError,
    onPersistSuccess,
    onPersistDetail,
    onVoiceSessionActiveChange,
    onStarterHintDismiss,
}) {
    const base = config.api.baseUrl.replace(/\/$/, "");
    const url = `${base}/collections/${encodeURIComponent(collectionId)}/owner-voice/webrtc`;

    const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    const audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    audioEl.setAttribute("playsinline", "true");
    document.body.appendChild(audioEl);

    pc.ontrack = (e) => {
        audioEl.srcObject = e.streams[0];
    };

    const ms = await navigator.mediaDevices.getUserMedia({
        audio: {
            echoCancellation: true,
            noiseSuppression: true,
        },
    });
    ms.getTracks().forEach((track) => pc.addTrack(track, ms));

    let starterHintDismissed = false;
    const maybeDismissStarterHint = () => {
        if (starterHintDismissed || typeof onStarterHintDismiss !== "function") return;
        starterHintDismissed = true;
        try {
            onStarterHintDismiss();
        } catch (_) {
            /* ignore */
        }
    };

    const dc = pc.createDataChannel("oai-events");
    dc.addEventListener("message", (e) => {
        let event;
        try {
            event = JSON.parse(e.data);
        } catch {
            return;
        }
        const t = event.type;
        if (
            t === "input_audio_buffer.speech_started" ||
            t === "input_audio_buffer.committed" ||
            t === "conversation.item.input_audio_transcription.completed" ||
            t === "response.output_audio.delta" ||
            t === "response.output_audio_transcript.delta" ||
            t === "response.audio.delta" ||
            t === "response.audio_transcript.delta"
        ) {
            maybeDismissStarterHint();
        }
        if (onDataChannelMessage) {
            onDataChannelMessage(event);
        }
        void handleOwnerCollectVoiceTools(
            event,
            dc,
            collectionId,
            accessToken,
            base,
            onPersistSuccess,
            onPersistDetail
        );
    });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const headers = {
        Accept: "application/sdp, application/json",
        "Content-Type": "application/json",
        ...authHeaders(accessToken),
    };

    const resp = await fetch(url, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
            collection_id: collectionId,
            sdp: offer.sdp || "",
            ...(clientShownPendingQuestion
                ? { client_shown_pending_question: clientShownPendingQuestion }
                : {}),
        }),
    });

    const ct = resp.headers.get("Content-Type") || "";
    if (!resp.ok) {
        let msg = await resp.text();
        try {
            const j = JSON.parse(msg);
            msg = j.description || j.detail || msg;
        } catch (_) {
            /* keep text */
        }
        const err = new Error(msg || `HTTP ${resp.status}`);
        stopAll(pc, ms, audioEl);
        throw err;
    }

    if (ct.includes("application/json")) {
        const j = await resp.json();
        stopAll(pc, ms, audioEl);
        throw new Error(j.description || j.detail || "Voice session error");
    }

    const answerSdp = await resp.text();
    await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
    if (typeof onVoiceSessionActiveChange === "function") {
        onVoiceSessionActiveChange(true);
    }

    pc.addEventListener("connectionstatechange", () => {
        if (pc.connectionState === "failed" && onError) {
            onError(new Error("WebRTC connection failed"));
        }
    });

    const stop = () => {
        try {
            dc.close();
        } catch (_) {
            /* ignore */
        }
        if (typeof onVoiceSessionActiveChange === "function") {
            onVoiceSessionActiveChange(false);
        }
        stopAll(pc, ms, audioEl);
    };

    return { pc, dc, audioElement: audioEl, stop };
}
